import {ConfigService, ErrorsService, ModelsList, StoreService} from 'shared';
import {Observable} from 'rxjs';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {map} from 'rxjs/operators';
import CustomStore from 'devextreme/data/custom_store';
import {LoadOptions} from 'devextreme/data/load_options';
import {catchError} from 'rxjs/operators';
import {throwError, of} from 'rxjs';
import DataSource from 'devextreme/data/data_source';

export interface RejectedField {
  field: string;
  issue: string;
  correct_value?: any;
}

export interface ReconstructedIntervention {
  id?: number;
  trailer_id: number;
  vehicle_id: number;
  field_id?: number;
  state: string;
  start_time: Date;
  stop_time: Date;
  last_working_point?: any;
  working_multipolygon?: any;
  working_area?: number;
  last_working_out_time?: Date;
  last_engine_state?: any;
  last_engine_state_group?: any;
  working_out_field_move_duration?: number;
  working_out_field_idle_duration?: number;
  working_out_field_off_duration?: number;
  working_in_field_move_duration?: number;
  working_in_field_idle_duration?: number;
  working_in_field_off_duration?: number;
  road_move_duration?: number;
  road_idle_duration?: number;
  road_off_duration?: number;
  last_fuel_level?: number;
  working_out_field_fuel_consumption?: number;
  working_in_field_fuel_consumption?: number;
  road_fuel_consumption?: number;
  working_in_field_speed_count?: number;
  working_in_field_speed_avg?: number;
  working_in_field_speed_max?: number;
  pressure_count?: number;
  pressure_accumulation?: number;
  pressure_avg?: number;
  pressure_max?: number;
  speed_avg?: number;
  speed_max?: number;
  odometer?: number;
  satellites?: number;
  gsm_level?: number;
  hdop?: number;
  created_at?: Date;
  updated_at?: Date;
  deleted_at?: Date;
  intervention_type?: string;
  confidence_score?: number;
  accuracy_rating?: number;
  validation_accuracy?: string;
  culture_id?: number;
  culture_name?: string;
  status?: string;
  validated_by?: number;
  validated_at?: Date;
  rejection_reason?: string;
  rejected_fields?: RejectedField[];
  field?: any;
  tractor_name?: string;
  trailer_name?: string;
  domain_name?: string;
  business_unit_name?: string;
  working_area_ha?: number;
  working_area_percent?: number;
  trailer_width?: number;
  total_duration?: number;
  total_working_duration?: number;
  stop_working_duration?: number;
  total_road_duration?: number;
  working_hectare_per_hour?: number;
  total_fuel_consumption?: number;
  fuel_consumption_per_hectare?: number;
  drive_working_duration?: number;
  working_multipolygon_geojson?: any;
  trajectory_linestring_geojson?: any;
}

@Injectable({
  deps: [HttpClient],
  providedIn: 'root'
})
export class ReconstructedInterventionService extends StoreService<ReconstructedIntervention> {
  private _customStore: CustomStore;

  constructor(http: HttpClient, config: ConfigService, errors: ErrorsService) {
    super(http, config, errors);
    // Create custom store manually since ReconstructedIntervention is not a BaseModel
    this._customStore = new CustomStore({
      key: 'id',
      load: (loadOptions: LoadOptions) => {
        let params = new HttpParams();
        
        // Convert DevExtreme load options to HTTP params
        if (loadOptions.filter) {
          // Apply filters from DevExtreme
          const filter = loadOptions.filter;
          if (Array.isArray(filter) && filter.length > 0) {
            filter.forEach((f: any) => {
              if (Array.isArray(f) && f.length === 3) {
                const [field, operator, value] = f;
                if (operator === '>=' || operator === '<=' || operator === '>=' || operator === '<') {
                  // Date filters
                  if (field === 'start_time') {
                    if (operator === '>=') {
                      params = params.set('from_date', this.formatDate(value));
                    }
                    if (operator === '<=') {
                      params = params.set('to_date', this.formatDate(value));
                    }
                  }
                }
              }
            });
          }
        }
        
        if (loadOptions.skip !== undefined) {
          params = params.set('skip', loadOptions.skip.toString());
        }
        if (loadOptions.take !== undefined) {
          params = params.set('take', loadOptions.take.toString());
        }
        if (loadOptions.sort) {
          const sort = loadOptions.sort[0];
          if (sort) {
            params = params.set('sort', sort.selector);
            params = params.set('order', sort.desc ? 'desc' : 'asc');
          }
        }
        if (loadOptions.searchValue) {
          params = params.set('search', loadOptions.searchValue);
        }

        return this.load(params).pipe(
          map((result: ModelsList<ReconstructedIntervention>) => {
            return {
              data: result.data || [],
              totalCount: result.totalCount || (result.data ? result.data.length : 0),
              custom: result.custom || {}
            };
          }),
          catchError(err => {
            this.errors.handle(err);
            return throwError(err);
          })
        ).toPromise();
      }
    });
  }

  protected load(queryParams?: HttpParams): Observable<ModelsList<ReconstructedIntervention>> {
    return this.http.get<ModelsList<any>>(this.config.apiVerUrl('/app/reconstructed-interventions'), {params: queryParams}).pipe(
      map(l => {
        // Handle null or undefined data
        if (!l) {
          l = { data: [], totalCount: 0, custom: {} };
        }
        if (!l.data) {
          l.data = [];
        }
        // Map and transform data from backend format to frontend format
        l.data = (l.data as any[]).map((m: any) => {
          // Build field object if field data exists (create even if some fields are null)
          let fieldObj: any = null;
          if (m.field_id != null || m.field_name != null || m.field_polygon_area != null) {
            const fieldPolygonArea = m.field_polygon_area != null ? parseFloat(String(m.field_polygon_area)) : null;
            fieldObj = {
              id: m.field_id || null,
              name: m.field_name || null,
              polygon_area: fieldPolygonArea,
              polygonAreaToHa: () => fieldPolygonArea ? fieldPolygonArea / 10000 : 0,
              color: m.field_color || '#4ef34a',
              createLayer: (options: any) => {
                // Simple field layer creation - would need proper Field model for full implementation
                return null; // Placeholder - field geometry handling would need proper Field model
              }
            };
          }

          // Helper to safely parse numbers
          const safeNum = (val: any, def: number = 0): number => {
            if (val == null) return def;
            const parsed = parseFloat(val);
            return isNaN(parsed) ? def : parsed;
          };

          const item: ReconstructedIntervention = {
            ...m,
            // Transform nested field structure
            field: fieldObj,
            // Map backend calculated fields - use explicit checks for 0 vs null
            total_duration: safeNum(m.total_duration, 0),
            total_working_duration: safeNum(m.total_working_duration, 0),
            drive_working_duration: safeNum(m.drive_working_duration, 0),
            total_road_duration: safeNum(m.total_road_duration, 0),
            working_area_ha: m.working_area_ha != null ? safeNum(m.working_area_ha) : (m.working_area != null ? safeNum(m.working_area) / 10000 : 0),
            working_area_percent: safeNum(m.working_area_percent, 0),
            // Map speed fields - backend already returns working_in_field_speed_avg/max, use them directly
            working_in_field_speed_avg: m.working_in_field_speed_avg != null ? safeNum(m.working_in_field_speed_avg) : (m.speed_avg != null ? safeNum(m.speed_avg) : null),
            working_in_field_speed_max: m.working_in_field_speed_max != null ? safeNum(m.working_in_field_speed_max) : (m.speed_max != null ? safeNum(m.speed_max) : null),
            // Calculate missing fields only if not provided
            stop_working_duration: m.stop_working_duration != null ? safeNum(m.stop_working_duration) : this.calculateStopWorkingDuration(m),
            working_hectare_per_hour: m.working_hectare_per_hour != null ? safeNum(m.working_hectare_per_hour) : this.calculateWorkingHectarePerHour(m),
            total_fuel_consumption: m.total_fuel_consumption != null ? safeNum(m.total_fuel_consumption) : this.calculateTotalFuelConsumption(m),
            fuel_consumption_per_hectare: m.fuel_consumption_per_hectare != null ? safeNum(m.fuel_consumption_per_hectare) : this.calculateFuelConsumptionPerHectare(m),
            // Ensure durations are numbers
            working_in_field_move_duration: safeNum(m.working_in_field_move_duration, 0),
            working_in_field_idle_duration: safeNum(m.working_in_field_idle_duration, 0),
            working_in_field_off_duration: safeNum(m.working_in_field_off_duration, 0),
            working_out_field_move_duration: safeNum(m.working_out_field_move_duration, 0),
            working_out_field_idle_duration: safeNum(m.working_out_field_idle_duration, 0),
            working_out_field_off_duration: safeNum(m.working_out_field_off_duration, 0),
            road_move_duration: safeNum(m.road_move_duration, 0),
            road_idle_duration: safeNum(m.road_idle_duration, 0),
            road_off_duration: safeNum(m.road_off_duration, 0),
            // Map geometry - working_multipolygon_geojson if available
            working_multipolygon_geojson: m.working_multipolygon_geojson || m.working_multipolygon || null,
            // Ensure other fields are properly typed
            trailer_width: safeNum(m.trailer_width),
            confidence_score: safeNum(m.confidence_score),
            accuracy_rating: safeNum(m.accuracy_rating),
            satellites: safeNum(m.satellites),
            gsm_level: safeNum(m.gsm_level),
            hdop: safeNum(m.hdop),
            odometer: safeNum(m.odometer),
          };
          // Convert dates
          if (item.start_time && typeof item.start_time === 'string') item.start_time = new Date(item.start_time);
          if (item.stop_time && typeof item.stop_time === 'string') item.stop_time = new Date(item.stop_time);
          if (item.created_at && typeof item.created_at === 'string') item.created_at = new Date(item.created_at);
          if (item.updated_at && typeof item.updated_at === 'string') item.updated_at = new Date(item.updated_at);
          if (item.validated_at && typeof item.validated_at === 'string') item.validated_at = new Date(item.validated_at);
          return item;
        });
        return l as ModelsList<ReconstructedIntervention>;
      }),
      catchError(err => {
        this.errors.handle(err);
        return throwError(err);
      })
    );
  }

  private calculateStopWorkingDuration(m: any): number {
    const safeNum = (val: any): number => {
      if (val == null) return 0;
      const parsed = parseFloat(val);
      return isNaN(parsed) ? 0 : parsed;
    };
    return safeNum(m.working_in_field_idle_duration) + 
           safeNum(m.working_in_field_off_duration) +
           safeNum(m.working_out_field_idle_duration) +
           safeNum(m.working_out_field_off_duration);
  }

  private calculateWorkingHectarePerHour(m: any): number {
    const safeNum = (val: any): number => {
      if (val == null) return 0;
      const parsed = parseFloat(val);
      return isNaN(parsed) ? 0 : parsed;
    };
    const workingAreaHa = m.working_area_ha != null ? safeNum(m.working_area_ha) : (m.working_area != null ? safeNum(m.working_area) / 10000 : 0);
    const totalWorkingDuration = m.total_working_duration != null ? safeNum(m.total_working_duration) : 
      (safeNum(m.working_in_field_move_duration) + 
       safeNum(m.working_in_field_idle_duration) + 
       safeNum(m.working_in_field_off_duration) +
       safeNum(m.working_out_field_move_duration) + 
       safeNum(m.working_out_field_idle_duration) + 
       safeNum(m.working_out_field_off_duration));
    if (totalWorkingDuration > 0) {
      return (workingAreaHa / totalWorkingDuration) * 3600; // Convert seconds to hours
    }
    return 0;
  }

  private calculateTotalFuelConsumption(m: any): number {
    const safeNum = (val: any): number => {
      if (val == null) return 0;
      const parsed = parseFloat(val);
      return isNaN(parsed) ? 0 : parsed;
    };
    return safeNum(m.working_in_field_fuel_consumption) + 
           safeNum(m.working_out_field_fuel_consumption) + 
           safeNum(m.road_fuel_consumption);
  }

  private calculateFuelConsumptionPerHectare(m: any): number {
    const safeNum = (val: any): number => {
      if (val == null) return 0;
      const parsed = parseFloat(val);
      return isNaN(parsed) ? 0 : parsed;
    };
    const totalFuel = this.calculateTotalFuelConsumption(m);
    const workingAreaHa = m.working_area_ha != null ? safeNum(m.working_area_ha) : (m.working_area != null ? safeNum(m.working_area) / 10000 : 0);
    if (workingAreaHa > 0) {
      return totalFuel / workingAreaHa;
    }
    return 0;
  }

  newDataSource(options?: any): DataSource {
    const all = options || {};
    all.store = this._customStore;
    return new DataSource(all);
  }

  private formatDate(date: Date): string {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  protected insert(values: ReconstructedIntervention): Observable<number> {
    return null;
  }

  protected remove(id): Observable<void> {
    return null;
  }

  protected update(id, values: ReconstructedIntervention): Observable<ReconstructedIntervention> {
    return null;
  }

  validate(id: number, validationAccuracy: string, interventionType: string, cultureId?: number): Observable<any> {
    return this.http.post<any>(this.config.apiVerUrl(`/app/reconstructed-interventions/${id}/validate`), {
      validation_accuracy: validationAccuracy,
      intervention_type: interventionType,
      culture_id: cultureId
    });
  }

  validateAndCreateFields(id: number, validationAccuracy?: string, interventionType?: string, cultureId?: number): Observable<any> {
    return this.http.post<any>(this.config.apiVerUrl(`/app/reconstructed-interventions/${id}/validate-and-create-fields`), {
      validation_accuracy: validationAccuracy,
      intervention_type: interventionType,
      culture_id: cultureId
    });
  }

  reject(id: number, rejectionReason: string, rejectedFields?: RejectedField[]): Observable<any> {
    return this.http.post<any>(this.config.apiVerUrl(`/app/reconstructed-interventions/${id}/reject`), {
      rejection_reason: rejectionReason,
      rejected_fields: rejectedFields || []
    });
  }

  updateInterventionType(id: number, interventionType: string): Observable<any> {
    return this.http.patch<any>(this.config.apiVerUrl(`/app/reconstructed-interventions/${id}/intervention-type`), {
      intervention_type: interventionType
    });
  }

  revoke(id: number): Observable<any> {
    return this.http.post<any>(this.config.apiVerUrl(`/app/reconstructed-interventions/${id}/revoke`), {});
  }

  patch(id: number, updates: Partial<ReconstructedIntervention>): Observable<ReconstructedIntervention> {
    return this.http.patch<ReconstructedIntervention>(
      this.config.apiVerUrl(`/app/reconstructed-interventions/${id}`),
      updates
    );
  }

}

